import { useEffect, useState } from "react";
import { 
  Folder, 
  BookOpen, 
  Layers, 
  MapPin, 
  Sparkles, 
  User, 
  ClipboardList,
  BookText,
  Hammer,
  Edit,
  Trash2
} from "lucide-react";
import Header from "../../components/Header";
import api from "../../connector/api";
import toast from "react-hot-toast";
import TextbookModal from "../../inventory/components/TextbookModal";
import ClassroomInventoryModal from "../../inventory/components/ClassroomInventoryModal";
import DeleteConfirmationModal from "../../inventory/components/DeleteConfirmationModal";

export default function MyRoom() {
    const [assignedClasses, setAssignedClasses] = useState([]);
    const [selectedClassId, setSelectedClassId] = useState(null); // Track ID instead of object instance to avoid stale reference bugs
    const [loading, setLoading] = useState(true);
    
    // Unified textbook modal visibility state
    const [isBookModalOpen, setIsBookModalOpen] = useState(false);
    const [editingTextbook, setEditingTextbook] = useState(null);
    const [bookFormData, setBookFormData] = useState({ 
        id: null,
        description: "",
        quantity_new: 0,
        quantity_good: 0,
        quantity_bad: 0,
        quantity_lost: 0,
        remarks: "" 
    });

    const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
    const [editingInventory, setEditingInventory] = useState(null);
    const [inventoryFormData, setInventoryFormData] = useState({
        id: null,
        item_type: "Chair",
        quantity_functional: 0,
        quantity_damaged: 0,
        remarks: ""
    });

    // Delete Modal State Architecture
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: null, type: null, label: "" });

    const fetchTeacherWorkspace = async () => {
        setLoading(true);
        try {
            // Option A: If your generic api instance doesn't have an interceptor, 
            // fetch token from localStorage or cookies manually here:
            const token = localStorage.getItem('userToken'); 
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

            // Pass config directly to the GET request
            const res = await api.get("my-room-workspace/", config);
            
            // Debug logger to trace structural payloads in your browser developer tools console
            console.log("React Web Workspace Payload Log:", res.data);

            // Solid fallback chain to extract arrays out of paginated vs unpaginated responses
            let normalizedData = [];
            if (res.data) {
                if (res.data.results && Array.isArray(res.data.results)) {
                    normalizedData = res.data.results;
                } else if (Array.isArray(res.data)) {
                    normalizedData = res.data;
                } else if (typeof res.data === 'object') {
                    // If backend returns a single dictionary object or nested array property wrapper
                    normalizedData = res.data.classes || res.data.data || [];
                }
            }
                
            setAssignedClasses(normalizedData);
            
            // Check array bounds safely before selecting fallback item index
            if (Array.isArray(normalizedData) && normalizedData.length > 0) {
                if (!selectedClassId) {
                    setSelectedClassId(normalizedData[0].id);
                } else {
                    // Keep the current ID selected if it still exists in the fresh list
                    const stillExists = normalizedData.some(c => c.id === selectedClassId);
                    if (!stillExists) {
                        setSelectedClassId(normalizedData[0].id);
                    }
                }
            } else {
                // Clear selection if data array returns clean but empty
                setSelectedClassId(null);
            }

        } catch (err) {
            console.error("Failed to compile teacher workspace matrix:", err);
            
            // Friendly UI feedback warning 
            if (err.response?.status === 401 || err.response?.status === 403) {
                toast.error("Session expired or unauthorized. Please re-authenticate.");
            } else {
                toast.error("Failed to stream secure room asset infrastructure.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeacherWorkspace();
        // Optional: Add selectedClassId to dependency array if you want to re-evaluate structural syncs
    }, []);
    // Derived State: Find current active class snapshot
    const selectedClass = assignedClasses.find(c => c.id === selectedClassId) || assignedClasses[0] || null;

    // Helper functions to safely sum row items dynamically
    const getClassTextbookCount = (cls) => {
        return cls?.textbooks?.reduce((sum, b) => {
            return sum + (Number(b.quantity_new) || 0) + (Number(b.quantity_good) || 0) + (Number(b.quantity_bad) || 0) + (Number(b.quantity_lost) || 0);
        }, 0) || 0;
    };

    const getClassInventoryCount = (cls) => {
        return cls?.inventory_items?.reduce((sum, item) => {
            return sum + (Number(item.quantity_functional) || 0) + (Number(item.quantity_damaged) || 0);
        }, 0) || 0;
    };

    // Global Aggregate Counters across all spaces
    const totalAllocatedTextbooks = assignedClasses.reduce((acc, curr) => acc + getClassTextbookCount(curr), 0);
    const totalClassroomInventoryItems = assignedClasses.reduce((acc, curr) => acc + getClassInventoryCount(curr), 0);

    const currentTeacherName = assignedClasses[0]?.employee_detail?.full_name || "Educator Workspace";

    const handleBookSubmit = async (dataToSave) => {
        if (!selectedClass) return;
        try {
            const payload = {
                classroom: selectedClass.id,
                description: dataToSave.description,
                quantity_new: Number(dataToSave.quantity_new) || 0,
                quantity_good: Number(dataToSave.quantity_good) || 0,
                quantity_bad: Number(dataToSave.quantity_bad) || 0,
                quantity_lost: Number(dataToSave.quantity_lost) || 0,
                remarks: dataToSave.remarks || "",
            };

            if (editingTextbook?.id) {
                await api.put(`textbooks/${editingTextbook.id}/`, payload);
                toast.success("Textbook matrix updated!");
            } else {
                await api.post("textbooks/", payload);
                toast.success("New textbook matrix allocated!");
            }

            setIsBookModalOpen(false);
            setEditingTextbook(null);
            fetchTeacherWorkspace();
        } catch (err) {
            console.error(err);
            toast.error("Network schema mutation failure");
        }
    };

    const handleInventorySubmit = async (dataToSave) => {
        if (!selectedClass) return;
        try {
            const payload = {
                classroom: selectedClass.id,
                item_type: dataToSave.item_type,
                quantity_functional: Number(dataToSave.quantity_functional) || 0,
                quantity_damaged: Number(dataToSave.quantity_damaged) || 0,
                remarks: dataToSave.remarks || "",
            };

            if (editingInventory?.id) {
                await api.put(`classroom-inventory/${editingInventory.id}/`, payload);
                toast.success("Inventory metrics updated successfully.");
            } else {
                await api.post("classroom-inventory/", payload);
                toast.success("Logged new asset inventory item!");
            }

            setIsInventoryModalOpen(false);
            setEditingInventory(null);
            fetchTeacherWorkspace();
        } catch (err) {
            console.error(err);
            toast.error("Failed to persist inventory data layer adjustments.");
        }
    };

    const handleEditBook = (book) => {
        setEditingTextbook(book);
        setBookFormData({
            id: book.id,
            description: book.description,
            quantity_new: book.quantity_new ?? 0,
            quantity_good: book.quantity_good ?? 0,
            quantity_bad: book.quantity_bad ?? 0,
            quantity_lost: book.quantity_lost ?? 0,
            remarks: book.remarks || "",
        });
        setIsBookModalOpen(true);
    };

    const handleEditInventory = (inv) => {
        setEditingInventory(inv);
        setInventoryFormData({
            id: inv.id,
            item_type: inv.item_type,
            quantity_functional: inv.quantity_functional ?? 0,
            quantity_damaged: inv.quantity_damaged ?? 0,
            remarks: inv.remarks || ""
        });
        setIsInventoryModalOpen(true);
    };

    const handleDeleteBookClick = (book) => {
        setDeleteTarget({ id: book.id, type: "textbook", label: book.description });
        setIsDeleteModalOpen(true);
    };

    const handleDeleteInventoryClick = (inv) => {
        setDeleteTarget({ id: inv.id, type: "inventory", label: inv.item_type });
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            if (deleteTarget.type === "textbook") {
                await api.delete(`textbooks/${deleteTarget.id}/`);
                toast.success("Textbook element purged safely.");
            } else if (deleteTarget.type === "inventory") {
                await api.delete(`classroom-inventory/${deleteTarget.id}/`);
                toast.success("Inventory item dropped safely.");
            }
            setIsDeleteModalOpen(false);
            fetchTeacherWorkspace();
        } catch (err) {
            console.error(err);
            toast.error("Failed to alter remote state matrix configuration node.");
        }
    };

    return (
        <div className="flex flex-col min-h-screen font-dynamic-title bg-slate-50/50 font-medium text-xs text-slate-600 tracking-normal overflow-auto">
            <header className="w-full bg-white border-b border-slate-100 z-30">
                <Header />
            </header>

            <main className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
                
                {/* Welcome Profile Header Card Panel */}
                <div className="bg-white p-6 border border-slate-100 rounded-xl shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <span className="text-[10px] font-mono font-black uppercase tracking-widest text-[#002855]/60 bg-[#002855]/5 px-2 py-0.5 rounded-md">
                            Custodian Ledger Workspace
                        </span>
                        <h1 className="text-base font-bold text-slate-900 tracking-tight mt-1">
                            {loading ? "Authenticating Digital Grid..." : `Custodian, ${currentTeacherName}`}
                        </h1>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                            Monitoring systemic physical workspace environments and assets under your immediate care.
                        </p>
                    </div>
                </div>

                {/* Core Matrix Metric Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-2xs flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-blue-50 text-[#002855]">
                            <Folder size={16} />
                        </div>
                        <div>
                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Assigned Classes</div>
                            <div className="text-sm font-black font-mono text-slate-900 mt-0.5">
                                {loading ? "..." : assignedClasses.length} Spaces
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-2xs flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600">
                            <ClipboardList size={16} />
                        </div>
                        <div>
                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Textbooks</div>
                            <div className="text-sm font-black font-mono text-slate-900 mt-0.5">
                                {loading ? "..." : totalAllocatedTextbooks} Units
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-2xs flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-purple-50 text-purple-600">
                            <Layers size={16} />
                        </div>
                        <div>
                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Classroom Inventory</div>
                            <div className="text-sm font-black font-mono text-slate-900 mt-0.5">
                                {loading ? "..." : `${totalClassroomInventoryItems} Units`}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Integrated Split Workspace Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                
                    {/* LEFT Panel */}
                    <section className="lg:col-span-4 space-y-3">
                        <div className="flex items-center gap-2 px-1">
                            <Folder size={14} className="text-slate-400" />
                            <h2 className="text-xs font-bold uppercase text-slate-400 tracking-wider">Your Linked Classes</h2>
                        </div>

                        {loading ? (
                            <div className="space-y-3">
                                {[1, 2].map((n) => (
                                    <div key={n} className="bg-white border border-slate-100 rounded-xl p-5 h-24 animate-pulse" />
                                ))}
                            </div>
                        ) : assignedClasses.length > 0 ? (
                            <div className="space-y-3">
                                {assignedClasses.map((cls) => {
                                    const isSelected = selectedClass?.id === cls.id;
                                    const classSpecificBooks = getClassTextbookCount(cls);
                                    return (
                                        <div 
                                            key={cls.id}
                                            onClick={() => setSelectedClassId(cls.id)}
                                            className={`border p-4 rounded-xl transition-all duration-200 flex flex-col justify-between group cursor-pointer ${
                                                isSelected 
                                                    ? "bg-white border-[#002855] shadow-xs" 
                                                    : "bg-white border-slate-100 hover:border-slate-200"
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="space-y-0.5">
                                                    <h3 className={`text-xs font-bold tracking-tight transition-colors ${
                                                        isSelected ? "text-[#002855]" : "text-slate-900 group-hover:text-[#002855]"
                                                    }`}>
                                                        {cls.name}  ({cls.year_detail?.name || ""})
                                                    </h3>
                                                    <p className="text-[10px] text-slate-400 flex items-center gap-1">
                                                        <MapPin size={10} className="text-slate-300" />
                                                        {cls.room_code || (cls.room ? `Room ID: ${cls.room}` : "No localized room assigned")}
                                                    </p>
                                                </div>
                                                <span className={`text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${
                                                    isSelected ? "bg-[#002855]/10 text-[#002855]" : "bg-slate-50 text-slate-400 border border-slate-100"
                                                }`}>
                                                    {classSpecificBooks} Resources
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center text-center p-8 bg-white rounded-2xl border border-dashed border-slate-200/80">
                                <Sparkles size={14} className="text-slate-300 mb-1" />
                                <h3 className="font-bold text-slate-800 text-[11px]">No classes assigned</h3>
                            </div>
                        )}
                    </section>

                    {/* RIGHT Panel */}
                    <section className="lg:col-span-8 space-y-3">
                        <div className="flex items-center gap-2 px-1">
                            <ClipboardList size={14} className="text-slate-400" />
                            <h2 className="text-xs font-bold uppercase text-slate-400 tracking-wider">
                                Live Asset Inspection Ledger
                            </h2>
                        </div>

                        {selectedClass ? (
                            <div className="bg-white border border-slate-100 rounded-xl p-5 shadow-2xs space-y-5">
                                
                                <div className="flex justify-between items-center border-b border-slate-50 pb-4">
                                    <div>
                                        <span className="text-[9px] font-black uppercase tracking-wider font-mono text-[#002855]/60 bg-[#002855]/5 px-2 py-0.5 rounded-md">
                                            Cluster Inspector
                                        </span>
                                        <h3 className="text-sm font-bold text-slate-900 tracking-tight mt-1">
                                            {selectedClass.name}
                                        </h3>
                                    </div>
                                </div>

                                {/* Textbooks Table */}
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                                            Curriculum Textbooks Allocation
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setEditingTextbook(null);
                                                setBookFormData({ 
                                                    id: null,
                                                    description: "",
                                                    quantity_new: 0,
                                                    quantity_good: 0,
                                                    quantity_bad: 0,
                                                    quantity_lost: 0,
                                                    remarks: "" 
                                                });
                                                setIsBookModalOpen(true);
                                            }}
                                            className="text-[10px] bg-blue-50 text-[#002855] hover:bg-blue-100 font-bold uppercase tracking-wider px-2 py-1 rounded transition-colors cursor-pointer"
                                        >
                                            + Add Textbook
                                        </button>
                                    </div>

                                    {selectedClass.textbooks && selectedClass.textbooks.length > 0 ? (
                                        <div className="overflow-x-auto border border-slate-100 rounded-xl">
                                        <table className="w-full text-[11px] border-collapse bg-white">
                                            <thead>
                                            <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider text-left">
                                                <th className="p-3">Textbook Title</th>
                                                <th className="p-3">Metrics</th>
                                                <th className="p-3 text-center w-20">Total</th>
                                                <th className="p-3 text-center w-16">Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                                            {selectedClass.textbooks.map((book) => {
                                                const calculatedTotal = (book.quantity_new || 0) + (book.quantity_good || 0) + (book.quantity_bad || 0) + (book.quantity_lost || 0);
                                                return (
                                                <tr key={book.id} className="hover:bg-slate-50/60 transition-colors align-middle">
                                                    <td className="p-3 text-slate-900 font-bold whitespace-normal max-w-[140px] truncate" title={book.description}>
                                                        {book.description}
                                                    </td>
                                                    <td className="p-3">
                                                    <div className="flex flex-wrap gap-1 items-center">
                                                        {book.quantity_new > 0 && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-blue-500/10 text-blue-700 border border-blue-500/10 uppercase">New: {book.quantity_new}</span>}
                                                        {book.quantity_good > 0 && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-700 border border-emerald-500/10 uppercase">Good: {book.quantity_good}</span>}
                                                        {book.quantity_bad > 0 && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-700 border border-amber-500/10 uppercase">Bad: {book.quantity_bad}</span>}
                                                        {book.quantity_lost > 0 && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-rose-500/10 text-rose-700 border border-rose-500/10 uppercase">Lost: {book.quantity_lost}</span>}
                                                    </div>
                                                    </td>
                                                    <td className="p-3 text-center">
                                                    <span className="inline-block px-2 py-0.5 bg-[#002855]/5 font-mono text-[#002855] font-bold rounded-md">
                                                        {book.total_quantity !== undefined ? book.total_quantity : calculatedTotal} U
                                                    </span>
                                                    </td>
                                                    <td className="p-3 text-center">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <button 
                                                            type="button" 
                                                            onClick={() => handleEditBook(book)} 
                                                            className="p-1 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                                        >
                                                            <Edit size={12} />
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            onClick={() => handleDeleteBookClick(book)} 
                                                            className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </div>
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                            </tbody>
                                        </table>
                                        </div>
                                    ) : (
                                        <p className="text-slate-400 text-[11px] font-medium p-2">
                                            No textbooks tracked inside this classroom container.
                                        </p>
                                    )}
                                </div>

                                {/* Hardware Inventory Table */}
                                <div className="space-y-2 pt-2 border-t border-slate-50">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                                            Classroom Hardware Inventory
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setEditingInventory(null);
                                                setInventoryFormData({
                                                    id: null,
                                                    item_type: "Chair",
                                                    quantity_functional: 0,
                                                    quantity_damaged: 0,
                                                    remarks: ""
                                                });
                                                setIsInventoryModalOpen(true);
                                            }}
                                            className="text-[10px] bg-blue-50 text-[#002855] hover:bg-blue-100 font-bold uppercase tracking-wider px-2 py-1 rounded transition-colors cursor-pointer"
                                        >
                                            + Add Inventory
                                        </button>
                                    </div>

                                    {selectedClass.inventory_items && selectedClass.inventory_items.length > 0 ? (
                                        <div className="border border-slate-100 rounded-xl overflow-x-auto">
                                            <table className="w-full text-[11px] border-collapse bg-white">
                                                <thead>
                                                    <tr className="bg-slate-50/70 border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider text-left">
                                                        <th className="p-3 font-bold">Item Type</th>
                                                        <th className="p-3 font-bold"> Status</th>
                                                        <th className="p-3 font-bold"> Remark</th>
                                                        <th className="p-3 font-bold text-center w-24">Total</th>
                                                        <th className="p-3 font-bold text-center w-20">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-50 text-slate-700">
                                                    {selectedClass.inventory_items.map((inv) => (
                                                        <tr key={inv.id} className="hover:bg-slate-50/20 align-middle">
                                                            <td className="p-3 text-slate-900 font-bold flex items-center gap-1.5">
                                                                {inv.item_type}
                                                            </td>
                                                            <td className="p-3">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border bg-emerald-50 text-emerald-700 border-emerald-100">
                                                                        <Layers size={10} /> Functional: {inv.quantity_functional}
                                                                    </span>
                                                                    {inv.quantity_damaged > 0 && (
                                                                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border bg-amber-50 text-amber-700 border-amber-100">
                                                                            <Hammer size={10} /> Damaged: {inv.quantity_damaged}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="p-3 text-center">
                                                                <span className="inline-block px-2.5 py-0.5 bg-slate-100 border border-slate-200/60 font-mono text-slate-800 font-bold rounded-md">
                                                                    {inv.remarks || "—"} 
                                                                </span>
                                                            </td>
                                                            <td className="p-3 text-center">
                                                                <span className="inline-block px-2.5 py-0.5 bg-slate-100 border border-slate-200/60 font-mono text-slate-800 font-bold rounded-md">
                                                                    {inv.total_items || (inv.quantity_functional + inv.quantity_damaged)} U
                                                                </span>
                                                            </td>
                                                            <td className="p-3 text-center">
                                                                <div className="flex items-center justify-center gap-1">
                                                                    <button 
                                                                        type="button" 
                                                                        onClick={() => handleEditInventory(inv)} 
                                                                        className="p-1 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                                                    >
                                                                        <Edit size={12} />
                                                                    </button>
                                                                    <button 
                                                                        type="button" 
                                                                        onClick={() => handleDeleteInventoryClick(inv)} 
                                                                        className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                                                    >
                                                                        <Trash2 size={12} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="text-slate-400 text-[11px] font-medium p-2">
                                            No asset items logged in this space yet.
                                        </p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl border p-6 text-center text-slate-400">
                                Please select a classroom space to review details.
                            </div>
                        )}
                    </section>
                </div>
            </main>

            {/* Custom Modals Wireframe Anchors */}
            <TextbookModal isOpen={isBookModalOpen} onClose={() => { setIsBookModalOpen(false); setEditingTextbook(null); }} onSave={handleBookSubmit} textbook={editingTextbook} formData={bookFormData} setFormData={setBookFormData} />
            <ClassroomInventoryModal isOpen={isInventoryModalOpen} onClose={() => { setIsInventoryModalOpen(false); setEditingInventory(null); }} onSave={handleInventorySubmit} inventoryItem={editingInventory} formData={inventoryFormData} setFormData={setInventoryFormData} />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal 
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                targetLabel={deleteTarget.label}
                targetType={deleteTarget.type}
            />
        </div>
    );
}